import React, { useMemo } from 'react';

import { Select } from '@chakra-ui/react';
import { type CountryCode } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import {
  getCountries,
  getCountryCallingCode,
} from 'react-phone-number-input/input';

import { getCountryCodeFromLanguage } from '@/utils/languageToCountry';

type CountrySelectProps = {
  value: CountryCode | undefined;
  onCountryChange: React.Dispatch<
    React.SetStateAction<CountryCode | undefined>
  >;
} & Omit<React.ComponentProps<typeof Select>, 'onChange' | 'value'>;

export const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onCountryChange,
  ...rest
}) => {
  const { i18n } = useTranslation();
  const defaultCountry: CountryCode = getCountryCodeFromLanguage(i18n.language);

  const selectedCountry = useMemo(() => {
    return value ?? defaultCountry;
  }, [defaultCountry, value]);

  const sortedCountries = useMemo(() => {
    const countriesMap = new Map<string, CountryCode>();

    getCountries().forEach((country) => {
      const callingCode = getCountryCallingCode(country);
      if (callingCode && !countriesMap.has(callingCode)) {
        countriesMap.set(callingCode, country);
      }
    });

    return Array.from(countriesMap.entries())
      .sort((a, b) => {
        const codeA = parseInt(a[0], 10);
        const codeB = parseInt(b[0], 10);
        return codeA - codeB;
      })
      .map(([, country]) => country);
  }, []);

  return (
    <Select
      borderRadius="xl"
      borderColor="gray.100"
      {...rest}
      onChange={(event) => {
        const selectedCountry = event.target.value as CountryCode;
        onCountryChange(selectedCountry);
      }}
      value={selectedCountry}
    >
      {sortedCountries.map((country) => {
        const callingCode = getCountryCallingCode(country);
        return (
          <option key={country} value={country}>
            {`+${callingCode}`}
          </option>
        );
      })}
    </Select>
  );
};
